import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, HelpCircle } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { useAdminContent } from "@/lib/adminContent";

const Faq = () => {
  const { t, dict, lang } = useI18n();
  const { faqItems } = useAdminContent(lang);
  const items = faqItems.length ? faqItems : dict.faq.items;
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" className="section" data-testid="faq-section">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7 }}
          className="text-center mb-8 sm:mb-10"
        >
          <div className="eyebrow">{t("faq.eyebrow")}</div>
          <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight mt-3 leading-[1.1]" data-testid="faq-title">
            {t("faq.title1")}
            <br className="hidden sm:block" />
            {" "}<span className="accent-text italic font-light">{t("faq.title2")}</span>
          </h2>
        </motion.div>

        <div className="space-y-3">
          {items.map((it, i) => {
            const isOpen = open === i;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className={`glass rounded-2xl overflow-hidden transition-all ${isOpen ? "border-[var(--accent)]/45" : ""}`}
                data-testid={`faq-item-${i}`}
              >
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full flex items-center gap-3 sm:gap-4 px-4 sm:px-7 py-4 sm:py-5 text-left"
                  data-testid={`faq-question-${i}`}
                >
                  <span className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                    isOpen ? "bg-[var(--accent)]/20 text-[var(--accent)]" : "bg-[var(--surface-2)] text-[var(--text-dim)]"
                  }`}>
                    <HelpCircle size={16} />
                  </span>
                  <span className="flex-1 font-medium text-sm sm:text-lg pr-2 sm:pr-4">{it.q}</span>
                  <motion.span
                    animate={{ rotate: isOpen ? 45 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="w-9 h-9 rounded-full border border-[var(--border)] flex items-center justify-center flex-shrink-0"
                  >
                    <Plus size={16} />
                  </motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 sm:px-7 pb-5 pl-[60px] sm:pl-[80px] text-[var(--text-dim)] text-sm sm:text-base leading-relaxed whitespace-pre-wrap" data-testid={`faq-answer-${i}`}>
                        {it.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Faq;
